import React, { useState, useEffect } from "react";
import DiagramaFibra from "./DiagramaFibra";
import EditorRed from "./EditorRed";
import DiagramaRed from "./DiagramaRed";
import FusionSplitter from "./FusionSplitter";
import FusionHilos from "./FusionHilos";
import FusionBuffers from "./FusionBuffers";
import { apiFetch, RC } from "../utils/api";

const COLORES_HILO = ["#2563EB","#F97316","#16A34A","#92400E","#6B7280","#F9FAFB","#DC2626","#111827","#CA8A04","#7C3AED","#DB2777","#0891B2"];
const NOMBRES = ["Azul","Naranja","Verde","Marron","Gris","Blanco","Rojo","Negro","Amarillo","Violeta","Rosado","Aqua"];
const PERDIDAS = {"1x2":3.5,"1x4":7.3,"1x8":10.5,"1x16":13.9,"1x32":17.2,"1x64":20.6};

// Número absoluto del hilo en toda la fibra
function hiloAbsoluto(buffer, hilo) {
  return (parseInt(buffer)-1)*12 + parseInt(hilo);
}

// Nombre completo de un hilo: "H1 Azul" o "H13 Azul (B2)"
function nombreHilo(buffer, hilo) {
  const abs = hiloAbsoluto(buffer, hilo);
  const color = NOMBRES[(parseInt(hilo)-1)%12];
  if(parseInt(buffer)===1) return `H${abs} ${color}`;
  return `H${abs} ${color} (B${buffer})`;
}

const S = {
  overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:400,display:"flex",flexDirection:"column",fontFamily:"Roboto,sans-serif"},
  header:{background:"#4C1D95",padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0},
  body:{flex:1,overflowY:"auto",padding:"12px"},
  card:{background:"#1a1a2e",border:"1px solid #2a2a4a",borderRadius:10,padding:"12px",marginBottom:10},
  cardTitle:{fontSize:11,color:"#C4B5FD",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8},
  row:{display:"flex",alignItems:"center",gap:8,marginBottom:6},
  label:{fontSize:12,color:"#9CA3AF",minWidth:110},
  val:{fontSize:13,color:"#F9FAFB",fontWeight:500},
  valOk:{fontSize:13,color:"#34D399",fontWeight:600},
  valWarn:{fontSize:13,color:"#FBBF24",fontWeight:600},
  valErr:{fontSize:13,color:"#F87171",fontWeight:600},
  divider:{height:1,background:"#2a2a4a",margin:"8px 0"},
  btnClose:{background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",borderRadius:8,padding:"6px 14px",fontSize:13,cursor:"pointer"},
  btnPrimary:{background:"#7C3AED",border:"none",color:"#fff",borderRadius:8,padding:"6px 14px",fontSize:13,cursor:"pointer",fontWeight:600},
  btnDanger:{background:"#7F1D1D",border:"none",color:"#FCA5A5",borderRadius:8,padding:"4px 10px",fontSize:12,cursor:"pointer"},
  btnSec:{background:"rgba(255,255,255,0.08)",border:"1px solid #374151",color:"#D1D5DB",borderRadius:8,padding:"6px 12px",fontSize:12,cursor:"pointer"},
  btnDir:{border:"1px solid #374151",borderRadius:6,padding:"4px 10px",fontSize:12,cursor:"pointer"},
  tab:{padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:500,cursor:"pointer",border:"none"},
  select:{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid #374151",fontSize:13,background:"#0f0f1a",color:"#F9FAFB",marginBottom:8},
  fusionRow:{display:"flex",alignItems:"center",gap:6,padding:"8px",borderRadius:6,background:"rgba(124,58,237,0.1)",border:"1px solid rgba(124,58,237,0.2)",marginBottom:6},
  splitterBox:{padding:"8px 14px",background:"rgba(124,58,237,0.2)",border:"1px solid rgba(124,58,237,0.4)",borderRadius:8,textAlign:"center"},
  seccion:{background:"rgba(124,58,237,0.08)",border:"1px solid rgba(124,58,237,0.2)",borderRadius:10,padding:"12px",marginBottom:12},
  seccionTitle:{fontSize:12,color:"#C4B5FD",fontWeight:600,marginBottom:10},
};

function TabBtn({active, onClick, children}) {
  return <button onClick={onClick} style={{...S.tab,background:active?"#7C3AED":"rgba(255,255,255,0.08)",color:active?"#fff":"#9CA3AF"}}>{children}</button>;
}

function PotenciaVal({val}) {
  if(val===null||val===undefined) return <span style={S.val}>—</span>;
  const n = parseFloat(val);
  if(n>=-27&&n<=-8) return <span style={S.valOk}>{val} dBm ✓</span>;
  if(n<-27) return <span style={S.valErr}>{val} dBm ✗ muy baja</span>;
  return <span style={S.valWarn}>{val} dBm ⚠</span>;
}

function DirBtn({value, onChange}) {
  return (
    <div style={{display:"flex",gap:4}}>
      <button onClick={()=>onChange("adelante")} style={{...S.btnDir,background:value==="adelante"?"#064E3B":"transparent",color:value==="adelante"?"#34D399":"#9CA3AF"}}>Salida →</button>
      <button onClick={()=>onChange("devolver")} style={{...S.btnDir,background:value==="devolver"?"#78350F":"transparent",color:value==="devolver"?"#FBBF24":"#9CA3AF"}}>← Entrada</button>
    </div>
  );
}

// Calcula hilos ocupados por dirección desde las fusiones reales en BD
function calcOcupados(fusiones, fibra_id) {
  const atras = [];    // ocupados hacia atrás (entrada / devolver)
  const adelante = []; // ocupados hacia adelante (salida / adelante)
  fusiones.forEach(f => {
    // splitter_entrada: ocupa el hilo en dirección ATRAS (viene de OLT)
    if(f.tipo==="splitter_entrada" && String(f.fibra_entrada_id)===String(fibra_id)){
      atras.push({buffer:parseInt(f.buffer_entrada), hilo:parseInt(f.hilo_entrada)});
    }
    // splitter_salida: ocupa el hilo en dirección ADELANTE (va hacia NAPs)
    else if(f.tipo==="splitter_salida" && String(f.fibra_salida_id)===String(fibra_id)){
      adelante.push({buffer:parseInt(f.buffer_salida), hilo:parseInt(f.hilo_salida)});
    }
    // hilo_hilo: ocupa según su dirección registrada
    else if(f.tipo==="hilo_hilo"){
      const dir = f.direccion||"devolver";
      if(String(f.fibra_entrada_id)===String(fibra_id)){
        if(dir==="devolver") atras.push({buffer:parseInt(f.buffer_entrada), hilo:parseInt(f.hilo_entrada)});
        else adelante.push({buffer:parseInt(f.buffer_entrada), hilo:parseInt(f.hilo_entrada)});
      }
      if(String(f.fibra_salida_id)===String(fibra_id)){
        adelante.push({buffer:parseInt(f.buffer_salida), hilo:parseInt(f.hilo_salida)});
      }
    }
    // buffer_completo: ocupa en adelante
    else if(f.tipo==="buffer_completo"){
      if(String(f.fibra_salida_id)===String(fibra_id)){
        adelante.push({buffer:parseInt(f.buffer_salida), hilo:parseInt(f.hilo_salida)});
      }
    }
  });
  return {atras, adelante};
}

// Selector de hilo con número absoluto y filtro por dirección
function HiloSelect({fibras, fibra_id, buffer, hilo, onFibra, onBuffer, onHilo, fusiones, direccion}) {
  const f = fibras.find(x=>String(x.id)===String(fibra_id));
  const tipo_hilos = f ? parseInt(f.tipo_hilos) : 12;
  const buffers = Math.ceil(tipo_hilos/12);
  const hilosEnBuffer = Math.min(12, tipo_hilos-(parseInt(buffer||1)-1)*12);

  const ocupados = (fibra_id && fusiones) ? calcOcupados(fusiones, fibra_id) : {atras:[], adelante:[]};

  const estaOcupado = (b, h) => {
    const lista = direccion==="devolver" ? ocupados.atras : ocupados.adelante;
    return lista.some(o=>o.buffer===parseInt(b)&&o.hilo===parseInt(h));
  };

  return (
    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
      <select style={{...S.select,flex:2,marginBottom:0}} value={fibra_id||""} onChange={e=>onFibra(e.target.value)}>
        <option value="">Fibra...</option>
        {fibras.map(f=><option key={f.id} value={f.id}>{f.nombre} ({f.tipo_hilos}h)</option>)}
      </select>
      {fibra_id&&<>
        <select style={{...S.select,flex:1,marginBottom:0}} value={buffer||1} onChange={e=>{onBuffer(e.target.value);onHilo(1);}}>
          {Array.from({length:buffers},(_,i)=>(
            <option key={i+1} value={i+1}>B{i+1} {NOMBRES[i%12]}</option>
          ))}
        </select>
        <select style={{...S.select,flex:1,marginBottom:0}} value={hilo||1} onChange={e=>onHilo(e.target.value)}>
          {Array.from({length:hilosEnBuffer},(_,i)=>{
            const h = i+1;
            const abs = hiloAbsoluto(buffer||1, h);
            const ocupado = estaOcupado(buffer||1, h);
            return <option key={h} value={h} disabled={ocupado}>{ocupado?"✗ ":""}H{abs} {NOMBRES[i%12]}{ocupado?" (usado)":""}</option>;
          })}
        </select>
      </>}
    </div>
  );
}

// Asistente persistente de fusiones
function AsistenteFusiones({mufa_id, splitters, fibras, fusionesExistentes, onGuardado, canEdit}) {

  // Inicializar desde fusiones existentes
  const initState = () => {
    const spEntrada = fusionesExistentes.find(f=>f.tipo==="splitter_entrada");
    const spSalidas = fusionesExistentes.filter(f=>f.tipo==="splitter_salida");
    const hilosHilo = fusionesExistentes.filter(f=>f.tipo==="hilo_hilo");
    const bufCompletos = [...new Set(fusionesExistentes.filter(f=>f.tipo==="buffer_completo").map(f=>parseInt(f.buffer_entrada)))];

    const sp_id_init = spEntrada?.splitter_id ? String(spEntrada.splitter_id) : (splitters[0]?String(splitters[0].id):"");
    const splitterSel = splitters.find(s=>String(s.id)===sp_id_init);
    const numSal = splitterSel ? parseInt(splitterSel.tipo.split("x")[1]) : 0;

    const salidasInit = Array.from({length:numSal},(_,i)=>{
      const sal = spSalidas.find(s=>parseInt(s.splitter_salida_num)===i+1);
      return {
        num: i+1,
        fibra_id: sal ? String(sal.fibra_salida_id) : "",
        buffer: sal ? sal.buffer_salida : 1,
        hilo: sal ? sal.hilo_salida : i+1,
        direccion: sal ? sal.direccion : "adelante",
        fusion_id: sal ? sal.id : null,
      };
    });

    const hilosInit = hilosHilo.map(h=>({
      id: h.id,
      fibra_e: String(h.fibra_entrada_id), buffer_e: h.buffer_entrada, hilo_e: h.hilo_entrada,
      fibra_s: String(h.fibra_salida_id),  buffer_s: h.buffer_salida,  hilo_s: h.hilo_salida,
      dir_e: h.direccion||"devolver", dir_s: "adelante",
    }));

    return {
      sp_id: sp_id_init,
      sp_fibra: spEntrada ? String(spEntrada.fibra_entrada_id) : "",
      sp_buffer: spEntrada ? spEntrada.buffer_entrada : 1,
      sp_hilo: spEntrada ? spEntrada.hilo_entrada : 1,
      sp_dir: spEntrada ? (spEntrada.direccion||"entrada") : "entrada",
      salidas: salidasInit,
      hilosSueltos: hilosInit,
      buffersCompletos: bufCompletos,
    };
  };

  const init = initState();
  const [sp_id, setSpId]               = useState(init.sp_id);
  const [sp_fibra, setSpFibra]         = useState(init.sp_fibra);
  const [sp_buffer, setSpBuffer]       = useState(init.sp_buffer);
  const [sp_hilo, setSpHilo]           = useState(init.sp_hilo);
  const [sp_dir, setSpDir]             = useState(init.sp_dir||"entrada");
  const [salidas, setSalidas]           = useState(init.salidas);
  const [hilosSueltos, setHilosSueltos] = useState(init.hilosSueltos);
  const [buffersCompletos, setBuffersCompletos] = useState(init.buffersCompletos);
  const [saving, setSaving]             = useState(false);
  const [nuevoHilo, setNuevoHilo]       = useState({fibra_e:"",buffer_e:1,hilo_e:1,fibra_s:"",buffer_s:1,hilo_s:1,dir_e:"devolver",dir_s:"adelante"});
  const [nuevaSalida, setNuevaSalida]   = useState({num:1,fibra_id:"",buffer:1,hilo:1,direccion:"adelante"});

  const aplicarSalida = () => {
    if(!nuevaSalida.fibra_id){ setError("Selecciona la fibra de destino"); return; }
    // Validar que el hilo de salida no esté ya usado
    const hAbsSal = (parseInt(nuevaSalida.buffer)-1)*12+parseInt(nuevaSalida.hilo);
    // Verificar si es el hilo del splitter
    const esHiloSplitter = sp_fibra && String(nuevaSalida.fibra_id)===String(sp_fibra) && parseInt(nuevaSalida.buffer)===parseInt(sp_buffer) && parseInt(nuevaSalida.hilo)===parseInt(sp_hilo);
    if(esHiloSplitter){ setError(`H${hAbsSal} ${nombreHilo(sp_buffer,sp_hilo)} está ocupado — es la entrada principal del Splitter ${splitterSel?.tipo}`); return; }
    const yaUsado = fusionesVirtuales.some(f=>String(f.fibra_salida_id)===String(nuevaSalida.fibra_id)&&parseInt(f.buffer_salida)===parseInt(nuevaSalida.buffer)&&parseInt(f.hilo_salida)===parseInt(nuevaSalida.hilo));
    if(yaUsado){ setError(`H${hAbsSal} ya está siendo usado en otra fusión`); return; }
    setError("");
    setSalidas(prev=>prev.map((s,i)=>s.num===nuevaSalida.num?{...s,fibra_id:nuevaSalida.fibra_id,buffer:nuevaSalida.buffer,hilo:nuevaSalida.hilo,direccion:nuevaSalida.direccion}:s));
    // Avanzar al siguiente número de salida disponible
    const sigNum = salidas.find(s=>s.num>nuevaSalida.num&&!s.fibra_id);
    setNuevaSalida({num:sigNum?sigNum.num:nuevaSalida.num,fibra_id:"",buffer:1,hilo:1,direccion:"adelante"});
  };

  const aplicarHiloSuelto = () => {
    if(!nuevoHilo.fibra_e||!nuevoHilo.fibra_s){ setError("Selecciona fibra de entrada y salida"); return; }
    // Validar que los hilos no estén ya usados
    const absE = (parseInt(nuevoHilo.buffer_e)-1)*12+parseInt(nuevoHilo.hilo_e);
    const absS = (parseInt(nuevoHilo.buffer_s)-1)*12+parseInt(nuevoHilo.hilo_s);
    // Usar calcOcupados para respetar dirección correctamente
    const ocupE = calcOcupados(fusionesVirtuales, nuevoHilo.fibra_e);
    const listaE = (nuevoHilo.dir_e||"devolver")==="devolver" ? ocupE.atras : ocupE.adelante;
    const yaUsadoE = listaE.some(o=>o.buffer===parseInt(nuevoHilo.buffer_e)&&o.hilo===parseInt(nuevoHilo.hilo_e));
    const ocupS = calcOcupados(fusionesVirtuales, nuevoHilo.fibra_s);
    const listaS = (nuevoHilo.dir_s||"adelante")==="adelante" ? ocupS.adelante : ocupS.atras;
    const yaUsadoS = listaS.some(o=>o.buffer===parseInt(nuevoHilo.buffer_s)&&o.hilo===parseInt(nuevoHilo.hilo_s));
    console.log("DEBUG aplicar:", {
      fibra_e: nuevoHilo.fibra_e, buffer_e: nuevoHilo.buffer_e, hilo_e: nuevoHilo.hilo_e, dir_e: nuevoHilo.dir_e,
      listaE, yaUsadoE,
      fusionesVirtuales_tipos: fusionesVirtuales.map(f=>f.tipo)
    });
    if(yaUsadoE){ setError(`H${absE} ${nombreHilo(nuevoHilo.buffer_e,nuevoHilo.hilo_e)} ya está ocupado en esa dirección`); return; }
    if(yaUsadoS){ setError(`H${absS} ya está usado en salida`); return; }
    setError("");
    setHilosSueltos(prev=>[...prev,{...nuevoHilo,id:null}]);
    // Reset a vacío para forzar nueva selección
    setNuevoHilo({fibra_e:"",buffer_e:1,hilo_e:1,fibra_s:"",buffer_s:1,hilo_s:1,dir_e:"devolver",dir_s:"adelante"});
  };
  const [error, setError]               = useState("");
  const [msgOk, setMsgOk]               = useState("");

  const splitterSel = splitters.find(s=>String(s.id)===String(sp_id));
  const numSalidas = splitterSel ? parseInt(splitterSel.tipo.split("x")[1]) : 0;

  // Cuando cambia el splitter seleccionado, actualizar salidas
  useEffect(()=>{
    if(!splitterSel) return;
    const n = parseInt(splitterSel.tipo.split("x")[1]);
    setSalidas(prev=>{
      if(prev.length===n) return prev;
      if(n < prev.length){
        // Reduccion — advertir y cortar salidas extras
        const salExtras = prev.filter(s=>s.num>n&&s.fibra_id);
        if(salExtras.length>0){
          if(!window.confirm(`Al reducir el splitter se perderán ${salExtras.length} salidas asignadas (${salExtras.map(s=>"Salida "+s.num).join(", ")}). ¿Continuar?`)) return prev;
        }
        return prev.slice(0,n).map((s,i)=>({...s,num:i+1}));
      }
      // Ampliacion — mantener existentes y agregar nuevas vacías
      return Array.from({length:n},(_,i)=>prev[i]||{num:i+1,fibra_id:"",buffer:1,hilo:i+1,direccion:"adelante",fusion_id:null});
    });
  },[sp_id]);


  const updSalida = (idx, key, val) => setSalidas(prev=>prev.map((s,i)=>i===idx?{...s,[key]:val}:s));
  const addHiloSuelto = () => setHilosSueltos(prev=>[...prev,{fibra_e:sp_fibra||"",buffer_e:1,hilo_e:1,fibra_s:sp_fibra||"",buffer_s:1,hilo_s:1,dir_e:"devolver",dir_s:"adelante",id:null}]);
  const updHiloSuelto = (idx,key,val) => setHilosSueltos(prev=>prev.map((h,i)=>i===idx?{...h,[key]:val}:h));
  const removeHiloSuelto = async(idx) => {
    const h = hilosSueltos[idx];
    if(h.id){ await apiFetch("/mufa_diagram.php?mufa_id="+mufa_id,{method:"DELETE",headers:{"Content-Type":"application/json"},body:JSON.stringify({accion:"fusion",id:h.id})}); }
    setHilosSueltos(prev=>prev.filter((_,i)=>i!==idx));
  };
  const toggleBuffer = (b) => setBuffersCompletos(prev=>prev.includes(b)?prev.filter(x=>x!==b):[...prev,b]);

  const fibraSpSel = fibras.find(f=>String(f.id)===String(sp_fibra));
  const tipo_hilos_sp = fibraSpSel ? parseInt(fibraSpSel.tipo_hilos) : 12;
  const totalBuffers = Math.ceil(tipo_hilos_sp/12);
  const bufferDesangrado = parseInt(sp_buffer||1);

  const guardar = async() => {
    setError(""); setMsgOk("");
    if(!sp_id){ setError("Selecciona el splitter"); return; }
    // Si ya existe splitter_entrada en BD no validar sp_fibra
    const yaHaySpEntrada = fusionesExistentes.some(f=>f.tipo==="splitter_entrada");
    if(!sp_fibra && !yaHaySpEntrada){ setError("Selecciona la fibra de entrada al splitter"); return; }
    setSaving(true);

    // Borrar fusiones existentes de este tipo para reemplazar
    const idsABorrar = fusionesExistentes.filter(f=>f.tipo==="splitter_entrada"||f.tipo==="splitter_salida"||f.tipo==="buffer_completo").map(f=>f.id);
    for(const id of idsABorrar){
      await apiFetch("/mufa_diagram.php?mufa_id="+mufa_id,{method:"DELETE",headers:{"Content-Type":"application/json"},body:JSON.stringify({accion:"fusion",id})});
    }

    const fusiones = [];

    // Entrada splitter
    fusiones.push({tipo:"splitter_entrada",fibra_entrada_id:parseInt(sp_fibra),buffer_entrada:parseInt(sp_buffer),hilo_entrada:parseInt(sp_hilo),fibra_salida_id:parseInt(sp_fibra),buffer_salida:parseInt(sp_buffer),hilo_salida:parseInt(sp_hilo),direccion:"adelante",splitter_id:parseInt(sp_id),splitter_salida_num:null,notas:"Entrada splitter"});

    // Salidas splitter
    for(const s of salidas){
      if(!s.fibra_id) continue;
      fusiones.push({tipo:"splitter_salida",fibra_entrada_id:parseInt(sp_fibra),buffer_entrada:parseInt(sp_buffer),hilo_entrada:parseInt(sp_hilo),fibra_salida_id:parseInt(s.fibra_id),buffer_salida:parseInt(s.buffer),hilo_salida:parseInt(s.hilo),direccion:s.direccion,splitter_id:parseInt(sp_id),splitter_salida_num:s.num,notas:"Salida "+s.num});
    }

    // Hilos sueltos (solo los nuevos sin id)
    for(const h of hilosSueltos){
      if(!h.fibra_e||!h.fibra_s||h.id) continue;
      fusiones.push({tipo:"hilo_hilo",fibra_entrada_id:parseInt(h.fibra_e),buffer_entrada:parseInt(h.buffer_e),hilo_entrada:parseInt(h.hilo_e),fibra_salida_id:parseInt(h.fibra_s),buffer_salida:parseInt(h.buffer_s),hilo_salida:parseInt(h.hilo_s),direccion:h.dir_e||"devolver",splitter_id:null,splitter_salida_num:null,notas:"dir_s:"+( h.dir_s||"adelante")});
    }

    // Buffers completos
    for(const b of buffersCompletos){
      const hilosEnBuf = Math.min(12,tipo_hilos_sp-(b-1)*12);
      for(let h=1;h<=hilosEnBuf;h++){
        fusiones.push({tipo:"buffer_completo",fibra_entrada_id:parseInt(sp_fibra),buffer_entrada:b,hilo_entrada:h,fibra_salida_id:parseInt(sp_fibra),buffer_salida:b,hilo_salida:h,direccion:"adelante",splitter_id:null,splitter_salida_num:null,notas:"Buffer "+b+" completo"});
      }
    }

    const r = await apiFetch("/mufa_diagram.php?mufa_id="+mufa_id,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({accion:"fusiones_lote",fusiones})});
    setSaving(false);
    if(r.success){ setMsgOk("Guardado correctamente — "+r.guardadas+" fusiones"); onGuardado(); }
    else setError(r.error||"Error al guardar");
  };


  // Fusiones virtuales = BD + pendientes en formulario (para filtrar hilos ya usados)
  const fusionesVirtuales = [
    ...fusionesExistentes,
    // Entrada del splitter actual — bloquea según dirección elegida
    ...(sp_fibra && sp_id ? [{
      tipo: sp_dir==="salida" ? "splitter_salida" : "splitter_entrada",
      fibra_entrada_id: sp_fibra,
      buffer_entrada: parseInt(sp_buffer),
      hilo_entrada: parseInt(sp_hilo),
      fibra_salida_id: sp_fibra,
      buffer_salida: parseInt(sp_buffer),
      hilo_salida: parseInt(sp_hilo),
    }] : []),
    // Salidas del splitter ya asignadas en el form
    ...salidas.filter(s=>s.fibra_id).map(s=>({
      tipo: "splitter_salida",
      fibra_entrada_id: sp_fibra,
      buffer_entrada: parseInt(sp_buffer),
      hilo_entrada: parseInt(sp_hilo),
      fibra_salida_id: s.fibra_id,
      buffer_salida: parseInt(s.buffer),
      hilo_salida: parseInt(s.hilo),
    })),
    // Salida que está siendo configurada ahora mismo
    ...(nuevaSalida.fibra_id ? [{
      tipo: "splitter_salida",
      fibra_entrada_id: sp_fibra,
      buffer_entrada: parseInt(sp_buffer),
      hilo_entrada: parseInt(sp_hilo),
      fibra_salida_id: nuevaSalida.fibra_id,
      buffer_salida: parseInt(nuevaSalida.buffer),
      hilo_salida: parseInt(nuevaSalida.hilo),
    }] : []),
    ...hilosSueltos.filter(h=>h.fibra_e&&h.fibra_s).map(h=>({
      fibra_entrada_id: h.fibra_e,
      buffer_entrada: parseInt(h.buffer_e),
      hilo_entrada: parseInt(h.hilo_e),
      fibra_salida_id: h.fibra_s,
      buffer_salida: parseInt(h.buffer_s),
      hilo_salida: parseInt(h.hilo_s),
    }))
  ];

  // Encuentra el primer hilo disponible usando calcOcupados correctamente
  const primerHiloDisponible = (fibra_id, buffer, direccion, fusions) => {
    const f = fibras.find(x=>String(x.id)===String(fibra_id));
    if(!f) return 1;
    const totalH = parseInt(f.tipo_hilos)||12;
    const hilosEnBuf = Math.min(12, totalH-(parseInt(buffer)-1)*12);
    const fusToUse = fusions || fusionesExistentes;
    const ocupados = calcOcupados(fusToUse, fibra_id);
    const lista = direccion==="devolver" ? ocupados.atras : ocupados.adelante;
    for(let h=1;h<=hilosEnBuf;h++){
      const usado = lista.some(o=>o.buffer===parseInt(buffer)&&o.hilo===h);
      if(!usado) return h;
    }
    return 1;
  };

  return (
    <div>
      {/* SECCION 1 */}
      <div style={S.seccion}>
        <div style={S.seccionTitle}>Sección 1 — Hilo de entrada al splitter</div>
        <select style={S.select} value={sp_id} onChange={e=>setSpId(e.target.value)} disabled={!canEdit}>
          <option value="">Seleccionar splitter...</option>
          {splitters.map(s=><option key={s.id} value={s.id}>Splitter {s.tipo} — Nivel {s.nivel} (pérdida {PERDIDAS[s.tipo]} dB)</option>)}
        </select>
        {sp_id&&<>
          <div style={{fontSize:11,color:"#9CA3AF",marginBottom:4}}>¿De dónde viene la potencia?</div>
          <div style={{display:"flex",gap:4,marginBottom:8}}>
            <button onClick={()=>{setSpDir("entrada");if(sp_fibra)setSpHilo(primerHiloDisponible(sp_fibra,sp_buffer,"devolver",fusionesExistentes));}} style={{...S.btnDir,background:sp_dir==="entrada"?"#78350F":"transparent",color:sp_dir==="entrada"?"#FBBF24":"#9CA3AF"}}>← Entrada (viene de OLT)</button>
            <button onClick={()=>{setSpDir("salida");if(sp_fibra)setSpHilo(primerHiloDisponible(sp_fibra,sp_buffer,"adelante",fusionesExistentes));}} style={{...S.btnDir,background:sp_dir==="salida"?"#064E3B":"transparent",color:sp_dir==="salida"?"#34D399":"#9CA3AF"}}>Salida → (viene de adelante)</button>
          </div>
          <div style={{fontSize:11,color:"#9CA3AF",marginBottom:4}}>Fibra + hilo que alimenta el splitter</div>
          <HiloSelect fibras={fibras} fibra_id={sp_fibra} buffer={sp_buffer} hilo={sp_hilo}
            onFibra={v=>{setSpFibra(v);setSpBuffer(1);setSpHilo(1);}}
            onBuffer={v=>{setSpBuffer(v);setSpHilo(1);}}
            onHilo={v=>setSpHilo(v)}
            fusiones={fusionesExistentes} direccion={sp_dir==="entrada"?"devolver":"adelante"}
          />
          {sp_fibra&&(
            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4,padding:"6px 10px",background:"rgba(30,123,191,0.15)",borderRadius:6}}>
              <div style={{width:10,height:10,borderRadius:"50%",background:COLORES_HILO[(parseInt(sp_hilo)-1)%12]}}/>
              <span style={{fontSize:12,color:"#60A5FA"}}>{nombreHilo(sp_buffer,sp_hilo)} {sp_dir==="entrada"?"← Entrada":"Salida →"} → Splitter {splitterSel?.tipo}</span>
            </div>
          )}
        </>}
      </div>

      {/* SECCION 2 */}
      {sp_id&&splitterSel&&(
        <div style={S.seccion}>
          <div style={S.seccionTitle}>Sección 2 — Salidas del splitter {splitterSel?.tipo} ({numSalidas} salidas)</div>
          {canEdit&&<>
            {/* Formulario compacto para asignar una salida */}
            <div style={{background:"rgba(0,0,0,0.2)",borderRadius:8,padding:"10px",marginBottom:10,border:"1px solid #2a2a4a"}}>
              <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:"#9CA3AF"}}>Salida #</span>
                <select style={{...S.select,flex:0,width:60,marginBottom:0}} value={nuevaSalida.num} onChange={e=>setNuevaSalida(x=>({...x,num:parseInt(e.target.value)}))}>
                  {Array.from({length:numSalidas},(_,i)=>(
                    <option key={i+1} value={i+1}>{i+1}</option>
                  ))}
                </select>
                <DirBtn value={nuevaSalida.direccion} onChange={v=>{
                const snapFus = [
                  ...fusionesExistentes,
                  ...(sp_fibra&&sp_id?[{tipo:"splitter_entrada",fibra_entrada_id:sp_fibra,buffer_entrada:parseInt(sp_buffer),hilo_entrada:parseInt(sp_hilo),fibra_salida_id:sp_fibra,buffer_salida:parseInt(sp_buffer),hilo_salida:parseInt(sp_hilo)}]:[]),
                  ...salidas.filter(s=>s.fibra_id).map(s=>({tipo:"splitter_salida",fibra_entrada_id:sp_fibra,buffer_entrada:parseInt(sp_buffer),hilo_entrada:parseInt(sp_hilo),fibra_salida_id:s.fibra_id,buffer_salida:parseInt(s.buffer),hilo_salida:parseInt(s.hilo)})),
                ];
                setNuevaSalida(x=>({...x,direccion:v,hilo:primerHiloDisponible(x.fibra_id,x.buffer,v,snapFus)}));
              }}/>
              </div>
              <HiloSelect fibras={fibras} fibra_id={nuevaSalida.fibra_id} buffer={nuevaSalida.buffer} hilo={nuevaSalida.hilo}
                onFibra={v=>setNuevaSalida(x=>({...x,fibra_id:v,buffer:1,hilo:primerHiloDisponible(v,1,x.direccion)}))}
                onBuffer={v=>setNuevaSalida(x=>({...x,buffer:v,hilo:1}))}
                onHilo={v=>setNuevaSalida(x=>({...x,hilo:v}))}
                fusiones={[
                  ...fusionesExistentes,
                  ...(sp_fibra&&sp_id?[{
                    tipo: sp_dir==="salida" ? "splitter_salida" : "splitter_entrada",
                    fibra_entrada_id:sp_fibra,buffer_entrada:parseInt(sp_buffer),hilo_entrada:parseInt(sp_hilo),
                    fibra_salida_id:sp_fibra,buffer_salida:parseInt(sp_buffer),hilo_salida:parseInt(sp_hilo)
                  }]:[]),
                  ...salidas.filter(s=>s.fibra_id).map(s=>({tipo:"splitter_salida",fibra_salida_id:s.fibra_id,buffer_salida:parseInt(s.buffer),hilo_salida:parseInt(s.hilo)})),
                ]}
                direccion={nuevaSalida.direccion}
              />
              <button onClick={aplicarSalida} style={{...S.btnPrimary,width:"100%",padding:"6px 0",marginTop:6,fontSize:12}}>
                Aplicar salida {nuevaSalida.num}
              </button>
            </div>
          </>}
          {/* Lista compacta de salidas asignadas */}
          {salidas.filter(s=>s.fibra_id).length===0&&<p style={{fontSize:11,color:"#4B5563",margin:0}}>Sin salidas asignadas.</p>}
          {salidas.filter(s=>s.fibra_id).map((s,i)=>{
            const abs = (parseInt(s.buffer)-1)*12+parseInt(s.hilo);
            const nom = ["Azul","Naranja","Verde","Marron","Gris","Blanco","Rojo","Negro","Amarillo","Violeta","Rosado","Aqua"][(parseInt(s.hilo)-1)%12];
            const col = ["#2563EB","#F97316","#16A34A","#92400E","#6B7280","#e0e0e0","#DC2626","#888","#CA8A04","#7C3AED","#DB2777","#0891B2"][(parseInt(s.hilo)-1)%12];
            return (
              <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:6,background:"rgba(124,58,237,0.08)",border:"1px solid #2a2a4a",marginBottom:4}}>
                <div style={{width:18,height:18,borderRadius:"50%",background:"#4C1D95",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:"#C4B5FD",fontWeight:700,flexShrink:0}}>{s.num}</div>
                <span style={{fontSize:10,color:"#C4B5FD"}}>Sal {s.num}</span>
                <span style={{fontSize:10,color:"#7C3AED"}}>→F→</span>
                <div style={{width:8,height:8,borderRadius:"50%",background:col,flexShrink:0}}/>
                <span style={{fontSize:11,color:col}}>H{abs} {nom}</span>
                <span style={{fontSize:10,color:s.direccion==="devolver"?"#FBBF24":"#34D399"}}>{s.direccion==="devolver"?"← Ent":"Sal →"}</span>
                <div style={{flex:1}}/>
                {canEdit&&<button onClick={()=>updSalida(salidas.indexOf(s),"fibra_id","")} style={S.btnDanger}>X</button>}
              </div>
            );
          })}
        </div>
      )}

      {/* SECCION 3 */}
      <div style={S.seccion}>
        <div style={S.seccionTitle}>Sección 3 — Hilos individuales (sin splitter)</div>
        {canEdit&&<>
          {/* Formulario compacto — un solo form */}
          <div style={{background:"rgba(0,0,0,0.2)",borderRadius:8,padding:"10px",marginBottom:10,border:"1px solid #2a2a4a"}}>
            <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap",marginBottom:6}}>
              <span style={{fontSize:11,color:"#9CA3AF",flexShrink:0}}>Entrada:</span>
              <DirBtn value={hilosSueltos[hilosSueltos.length]?.dir_e||nuevoHilo.dir_e||"devolver"} onChange={v=>setNuevoHilo(x=>({...x,dir_e:v,hilo_e:1,fibra_e:""}))}/>
            </div>
            <HiloSelect fibras={fibras} fibra_id={nuevoHilo.fibra_e} buffer={nuevoHilo.buffer_e} hilo={nuevoHilo.hilo_e}
              onFibra={v=>setNuevoHilo(x=>({...x,fibra_e:v,buffer_e:1,hilo_e:1}))}
              onBuffer={v=>setNuevoHilo(x=>({...x,buffer_e:v,hilo_e:1}))}
              onHilo={v=>setNuevoHilo(x=>({...x,hilo_e:v}))}
              fusiones={fusionesVirtuales} direccion={nuevoHilo.dir_e||"devolver"}
            />
            <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap",marginBottom:6,marginTop:4}}>
              <span style={{fontSize:11,color:"#9CA3AF",flexShrink:0}}>Salida:</span>
              <DirBtn value={nuevoHilo.dir_s||"adelante"} onChange={v=>setNuevoHilo(x=>({...x,dir_s:v,hilo_s:1,fibra_s:""}))}/>
            </div>
            <HiloSelect fibras={fibras} fibra_id={nuevoHilo.fibra_s} buffer={nuevoHilo.buffer_s} hilo={nuevoHilo.hilo_s}
              onFibra={v=>setNuevoHilo(x=>({...x,fibra_s:v,buffer_s:1,hilo_s:1}))}
              onBuffer={v=>setNuevoHilo(x=>({...x,buffer_s:v,hilo_s:1}))}
              onHilo={v=>setNuevoHilo(x=>({...x,hilo_s:v}))}
              fusiones={fusionesVirtuales} direccion={nuevoHilo.dir_s||"adelante"}
            />
            {error&&<p style={{color:"#F87171",fontSize:11,margin:"4px 0 0",padding:"4px 8px",background:"rgba(239,68,68,0.1)",borderRadius:4}}>{error}</p>}
            <button onClick={aplicarHiloSuelto} style={{...S.btnPrimary,width:"100%",padding:"6px 0",marginTop:6,fontSize:12}}>
              Aplicar fusión
            </button>
          </div>
        </>}
        {/* Lista compacta de fusiones hilo-hilo */}
        {hilosSueltos.length===0&&<p style={{fontSize:11,color:"#4B5563",margin:0}}>Sin hilos individuales.</p>}
        {hilosSueltos.map((h,i)=>{
          const absE = (parseInt(h.buffer_e)-1)*12+parseInt(h.hilo_e);
          const absS = (parseInt(h.buffer_s)-1)*12+parseInt(h.hilo_s);
          const nomE = ["Azul","Naranja","Verde","Marron","Gris","Blanco","Rojo","Negro","Amarillo","Violeta","Rosado","Aqua"][(parseInt(h.hilo_e)-1)%12];
          const nomS = ["Azul","Naranja","Verde","Marron","Gris","Blanco","Rojo","Negro","Amarillo","Violeta","Rosado","Aqua"][(parseInt(h.hilo_s)-1)%12];
          const cE = ["#2563EB","#F97316","#16A34A","#92400E","#6B7280","#e0e0e0","#DC2626","#888","#CA8A04","#7C3AED","#DB2777","#0891B2"][(parseInt(h.hilo_e)-1)%12];
          const cS = ["#2563EB","#F97316","#16A34A","#92400E","#6B7280","#e0e0e0","#DC2626","#888","#CA8A04","#7C3AED","#DB2777","#0891B2"][(parseInt(h.hilo_s)-1)%12];
          return (
            <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:6,background:"rgba(124,58,237,0.08)",border:"1px solid #2a2a4a",marginBottom:4}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:cE,flexShrink:0}}/>
              <span style={{fontSize:11,color:cE}}>H{absE} {nomE}</span>
              <span style={{fontSize:10,color:"#FBBF24"}}>{(h.dir_e||"devolver")==="devolver"?"← Ent":"→ Sal"}</span>
              <span style={{fontSize:10,color:"#7C3AED"}}>→F→</span>
              <span style={{fontSize:10,color:"#34D399"}}>{(h.dir_s||"adelante")==="adelante"?"Sal →":"← Ent"}</span>
              <div style={{width:8,height:8,borderRadius:"50%",background:cS,flexShrink:0}}/>
              <span style={{fontSize:11,color:cS}}>H{absS} {nomS}</span>
              <div style={{flex:1}}/>
              {canEdit&&<button onClick={()=>removeHiloSuelto(i)} style={S.btnDanger}>X</button>}
            </div>
          );
        })}
      </div>

      {/* SECCION 4 */}
      {sp_fibra&&totalBuffers>1&&(
        <div style={S.seccion}>
          <div style={S.seccionTitle}>Sección 4 — Búferes completos que siguen derecho</div>
          <p style={{fontSize:11,color:"#9CA3AF",margin:"0 0 10px"}}>
            Búferes que no se tocan — pasan directo hacia adelante. Genera {12} fusiones por búfer automáticamente.
          </p>
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {Array.from({length:totalBuffers},(_,b)=>{
              const bufNum = b+1;
              if(bufNum===bufferDesangrado) return (
                <div key={bufNum} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:8,border:"1px solid #374151",opacity:0.4}}>
                  <div style={{width:12,height:12,borderRadius:"50%",background:COLORES_HILO[b%12]}}/>
                  <span style={{fontSize:12,color:"#6B7280"}}>Buffer {bufNum} — {NOMBRES[b%12]} (desangrado)</span>
                </div>
              );
              const sel = buffersCompletos.includes(bufNum);
              const hilosEnBuf = Math.min(12,tipo_hilos_sp-(b)*12);
              return (
                <button key={bufNum} onClick={()=>canEdit&&toggleBuffer(bufNum)} style={{
                  display:"flex",alignItems:"center",gap:6,padding:"8px 12px",
                  borderRadius:8,border:"1px solid "+(sel?"#7C3AED":"#374151"),
                  background:sel?"rgba(124,58,237,0.2)":"transparent",
                  cursor:canEdit?"pointer":"default",
                }}>
                  <div style={{width:12,height:12,borderRadius:"50%",background:COLORES_HILO[b%12]}}/>
                  <span style={{fontSize:12,color:sel?"#C4B5FD":"#9CA3AF"}}>B{bufNum} {NOMBRES[b%12]} ({hilosEnBuf}h)</span>
                  {sel&&<span style={{fontSize:10,color:"#7C3AED"}}>✓</span>}
                </button>
              );
            })}
          </div>
          {buffersCompletos.length>0&&(
            <p style={{fontSize:11,color:"#7C3AED",margin:"8px 0 0"}}>
              {buffersCompletos.length} búfer(es) → {buffersCompletos.reduce((acc,b)=>acc+Math.min(12,tipo_hilos_sp-(b-1)*12),0)} fusiones automáticas
            </p>
          )}
        </div>
      )}

      {canEdit&&<>
        {error&&<p style={{color:"#F87171",fontSize:12,margin:"0 0 8px"}}>{error}</p>}
        {msgOk&&<p style={{color:"#34D399",fontSize:12,margin:"0 0 8px"}}>{msgOk}</p>}
        <button onClick={guardar} disabled={saving} style={{...S.btnPrimary,width:"100%",padding:"10px 0",marginTop:4}}>
          {saving?"Guardando...":"Guardar cambios"}
        </button>
      </>}
    </div>
  );
}

export default function MufaDiagram({mufa, equipos, onClose, user}) {
  const [data,setData]         = useState(null);
  const [loading,setLoading]   = useState(true);
  const [error,setError]       = useState(null);
  const [tab,setTab]           = useState("resumen");
  const [subTabFus,setSubTabFus]= useState("splitter");
  const [saving,setSaving]     = useState(false);
  const [showSplitterForm, setShowSplitterForm] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [splitterForm, setSplitterForm] = useState({tipo:"1x8",nivel:1,splitter_padre_id:"",puerto_padre:"",notas:""});

  const canEdit = user && RC.includes(user.rol);

  const cargar = ()=>{
    setLoading(true);
    apiFetch("/mufa_diagram.php?mufa_id="+mufa.id).then(d=>{
      if(d.success) setData(d);
      else setError(d.error||"Error al cargar");
      setLoading(false);
    }).catch(()=>{ setError("Error de conexion"); setLoading(false); });
  };

  useEffect(()=>{ cargar(); },[mufa.id]);

  const guardarSplitter = async()=>{
    setSaving(true);
    const r = await apiFetch("/mufa_diagram.php?mufa_id="+mufa.id,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
      accion:"splitter", tipo:splitterForm.tipo, nivel:parseInt(splitterForm.nivel),
      splitter_padre_id:splitterForm.splitter_padre_id||null, puerto_padre:splitterForm.puerto_padre||null,
      fibra_entrada_id:parseInt(splitterForm.fibra_entrada_id)||null,
      buffer_entrada:parseInt(splitterForm.buffer_entrada)||1,
      hilo_entrada:parseInt(splitterForm.hilo_entrada)||1,
      notas:"",
    })});
    if(r.success){
      // Guardar automáticamente la fusion splitter_entrada
      if(splitterForm.fibra_entrada_id){
        await apiFetch("/mufa_diagram.php?mufa_id="+mufa.id,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
          accion:"fusion", tipo:"splitter_entrada",
          fibra_entrada_id:parseInt(splitterForm.fibra_entrada_id),
          buffer_entrada:parseInt(splitterForm.buffer_entrada),
          hilo_entrada:parseInt(splitterForm.hilo_entrada),
          fibra_salida_id:parseInt(splitterForm.fibra_entrada_id),
          buffer_salida:parseInt(splitterForm.buffer_entrada),
          hilo_salida:parseInt(splitterForm.hilo_entrada),
          direccion:splitterForm.dir_entrada||"devolver",
          dir_salida:splitterForm.dir_entrada||"devolver",
          splitter_id:r.id,
          splitter_salida_num:null, notas:"",
        })});
      }
      setShowSplitterForm(false);
      setSplitterForm({tipo:'1x8',nivel:1,splitter_padre_id:'',puerto_padre:'',fibra_entrada_id:'',buffer_entrada:1,hilo_entrada:1,dir_entrada:'devolver'});
      cargar();
    } else alert(r.error||"Error");
    setSaving(false);
  };

  const eliminarSplitter = async(id)=>{
    if(!window.confirm("Eliminar splitter?")) return;
    await apiFetch("/mufa_diagram.php?mufa_id="+mufa.id,{method:"DELETE",headers:{"Content-Type":"application/json"},body:JSON.stringify({accion:"splitter",id})});
    cargar();
  };

  const eliminarFusion = async(id)=>{
    if(!window.confirm("Eliminar fusion?")) return;
    await apiFetch("/mufa_diagram.php?mufa_id="+mufa.id,{method:"DELETE",headers:{"Content-Type":"application/json"},body:JSON.stringify({accion:"fusion",id})});
    cargar();
  };

  const todasFibras = data ? [...(data.fibras_entrada||[]).map(f=>({...f,es_desangre:false})),...(data.fibras_salida||[]).map(f=>({...f,es_desangre:false})),...(data.fibras_paso||[]).map(f=>({...f,es_desangre:true}))] : [];

  const getTipoLabel = (f) => {
    if(f.tipo==="splitter_entrada") return "→ Entrada splitter";
    if(f.tipo==="splitter_salida") return (f.direccion==="devolver"?"← ":"→ ")+"Salida "+f.splitter_salida_num+" splitter";
    if(f.tipo==="buffer_completo") return "→ Buffer completo";
    return f.direccion==="adelante"?"Salida →":"← Entrada";
  };

  return (
    <div style={S.overlay}>
      <div style={S.header}>
        <div>
          <p style={{margin:0,color:"#fff",fontWeight:600,fontSize:15}}>{mufa.nombre}</p>
          <p style={{margin:0,color:"#C4B5FD",fontSize:11}}>
            Mufa · {mufa.tipo_instalacion} · {data?((data.splitters||[]).length+" splitters · "+(data.fusiones||[]).length+" fusiones"):"cargando..."}
          </p>
        </div>
        <button onClick={onClose} style={S.btnClose}>Cerrar</button>
      </div>

      {loading&&<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}><p style={{color:"#C4B5FD"}}>Cargando...</p></div>}
      {error&&<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}><p style={{color:"#F87171"}}>{error}</p></div>}

      {!loading&&!error&&data&&(
        <div style={S.body}>
          <div style={{display:"flex",gap:6,marginBottom:12,overflowX:"auto"}}>
            <TabBtn active={tab==="resumen"}   onClick={()=>setTab("resumen")}>Resumen</TabBtn>
            <TabBtn active={tab==="splitters"} onClick={()=>setTab("splitters")}>Splitters</TabBtn>
            <TabBtn active={tab==="fusiones"}  onClick={()=>setTab("fusiones")}>Fusiones</TabBtn>
            <TabBtn active={tab==="fibras"}    onClick={()=>setTab("fibras")}>Fibras</TabBtn>
            <TabBtn active={tab==="optico"}    onClick={()=>setTab("optico")}>Optico</TabBtn>
            <TabBtn active={tab==="recorrido"} onClick={()=>setTab("recorrido")}>Recorrido</TabBtn>
            <button onClick={()=>setEditorOpen(true)} style={{padding:"6px 14px",background:"#059669",color:"#fff",border:"none",borderRadius:8,fontSize:12,cursor:"pointer",fontWeight:600,marginLeft:8}}>✎ Editor</button>
          </div>

          {/* RESUMEN */}
          {tab==="resumen"&&<>
            <div style={S.card}>
              <div style={S.cardTitle}>Informacion</div>
              <div style={S.row}><span style={S.label}>Nombre</span><span style={S.val}>{mufa.nombre}</span></div>
              <div style={S.row}><span style={S.label}>Instalacion</span><span style={S.val}>{mufa.tipo_instalacion}</span></div>
              <div style={S.row}><span style={S.label}>Estado</span><span style={{...S.val,color:mufa.estado==="activo"?"#34D399":"#FBBF24"}}>{mufa.estado}</span></div>
              <div style={S.row}><span style={S.label}>OLT</span><span style={S.val}>{data.mufa.olt_nombre||"Sin OLT"}</span></div>
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>Fibras conectadas</div>
              {data.fibras_entrada.length>0&&<><p style={{fontSize:11,color:"#34D399",margin:"0 0 6px",fontWeight:600}}>ENTRADAS</p>{data.fibras_entrada.map(f=><div key={f.id} style={S.row}><div style={{width:8,height:8,borderRadius:"50%",background:"#34D399",flexShrink:0}}/><span style={S.val}>{f.nombre}</span><span style={{fontSize:11,color:"#6B7280"}}>{f.tipo_hilos}h</span></div>)}</>}
              {data.fibras_salida.length>0&&<><p style={{fontSize:11,color:"#60A5FA",margin:"8px 0 6px",fontWeight:600}}>SALIDAS</p>{data.fibras_salida.map(f=><div key={f.id} style={S.row}><div style={{width:8,height:8,borderRadius:"50%",background:"#60A5FA",flexShrink:0}}/><span style={S.val}>{f.nombre}</span><span style={{fontSize:11,color:"#6B7280"}}>{f.tipo_hilos}h</span></div>)}</>}
              {data.fibras_paso.length>0&&<><p style={{fontSize:11,color:"#FBBF24",margin:"8px 0 6px",fontWeight:600}}>PASAN DE LARGO</p>{data.fibras_paso.map(f=><div key={f.id} style={S.row}><div style={{width:8,height:8,borderRadius:"50%",background:"#FBBF24",flexShrink:0}}/><span style={S.val}>{f.nombre}</span><span style={{fontSize:11,color:"#6B7280"}}>{f.tipo_hilos}h</span></div>)}</>}
              {todasFibras.length===0&&<p style={{color:"#6B7280",fontSize:12,margin:0}}>Sin fibras detectadas.</p>}
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>Resumen interno</div>
              <div style={S.row}><span style={S.label}>Splitters</span><span style={S.val}>{data.splitters.length}</span></div>
              <div style={S.row}><span style={S.label}>Fusiones</span><span style={S.val}>{data.fusiones.length}</span></div>
              <div style={S.row}><span style={S.label}>Potencia salida</span><PotenciaVal val={data.potencia_salida}/></div>
            </div>
          </>}

          {/* SPLITTERS */}
          {tab==="splitters"&&<>
            <div style={S.card}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={S.cardTitle}>Splitters internos</div>
                {canEdit&&<button onClick={()=>setShowSplitterForm(!showSplitterForm)} style={S.btnPrimary}>+ Agregar</button>}
              </div>
              {showSplitterForm&&(
                <div style={{background:"rgba(124,58,237,0.1)",border:"1px solid rgba(124,58,237,0.3)",borderRadius:8,padding:"12px",marginBottom:12}}>
                  <div style={{fontSize:11,color:"#9CA3AF",marginBottom:4}}>Tipo</div>
                  <select style={S.select} value={splitterForm.tipo} onChange={e=>setSplitterForm(x=>({...x,tipo:e.target.value}))}>
                    {["1x2","1x4","1x8","1x16","1x32"].map(t=><option key={t} value={t}>{t} — {PERDIDAS[t]} dB</option>)}
                  </select>
                  <div style={{fontSize:11,color:"#9CA3AF",marginBottom:4}}>Nivel</div>
                  <select style={S.select} value={splitterForm.nivel} onChange={e=>setSplitterForm(x=>({...x,nivel:e.target.value}))}>
                    <option value={1}>Nivel 1 — primario (desde fibra)</option>
                    <option value={2}>Nivel 2 — secundario (desde splitter N1)</option>
                    <option value={3}>Nivel 3 — terciario (desde splitter N2)</option>
                  </select>
                  {parseInt(splitterForm.nivel)>1&&data.splitters.length>0&&<>
                    <div style={{fontSize:11,color:"#9CA3AF",marginBottom:4}}>Splitter padre</div>
                    <select style={S.select} value={splitterForm.splitter_padre_id} onChange={e=>setSplitterForm(x=>({...x,splitter_padre_id:e.target.value}))}>
                      <option value="">Seleccionar...</option>
                      {data.splitters.filter(s=>s.nivel<parseInt(splitterForm.nivel)).map(s=><option key={s.id} value={s.id}>Splitter {s.tipo} N{s.nivel}</option>)}
                    </select>
                    {splitterForm.splitter_padre_id&&<>
                      <div style={{fontSize:11,color:"#9CA3AF",marginBottom:4}}>Puerto del padre</div>
                      <select style={S.select} value={splitterForm.puerto_padre} onChange={e=>setSplitterForm(x=>({...x,puerto_padre:e.target.value}))}>
                        <option value="">Puerto...</option>
                        {Array.from({length:parseInt((data.splitters.find(s=>String(s.id)===String(splitterForm.splitter_padre_id))||{tipo:"1x2"}).tipo.split("x")[1])},(_,i)=><option key={i+1} value={i+1}>Puerto {i+1}</option>)}
                      </select>
                    </>}
                  </>}
                  <div style={{fontSize:11,color:"#9CA3AF",marginBottom:4,marginTop:8}}>Fibra de entrada al splitter</div>
                  <select style={S.select} value={splitterForm.fibra_entrada_id||""} onChange={e=>setSplitterForm(x=>({...x,fibra_entrada_id:e.target.value,buffer_entrada:1,hilo_entrada:1}))}>
                    <option value="">Selecciona fibra...</option>
                    {todasFibras.map(f=><option key={f.id} value={f.id}>{f.nombre} ({f.tipo_hilos}h)</option>)}
                  </select>
                  {splitterForm.fibra_entrada_id&&<>
                    <div style={{fontSize:11,color:"#9CA3AF",marginBottom:4}}>Buffer</div>
                    <select style={S.select} value={splitterForm.buffer_entrada||1} onChange={e=>setSplitterForm(x=>({...x,buffer_entrada:e.target.value,hilo_entrada:1}))}>
                      {Array.from({length:Math.ceil(parseInt(todasFibras.find(f=>String(f.id)===String(splitterForm.fibra_entrada_id))?.tipo_hilos||12)/12)},(_,i)=>(
                        <option key={i+1} value={i+1}>B{i+1} {["Azul","Naranja","Verde","Marron","Gris","Blanco","Rojo","Negro","Amarillo","Violeta","Rosado","Aqua"][i%12]}</option>
                      ))}
                    </select>
                    <div style={{fontSize:11,color:"#9CA3AF",marginBottom:4}}>Hilo de entrada</div>
                    <select style={S.select} value={splitterForm.hilo_entrada||1} onChange={e=>setSplitterForm(x=>({...x,hilo_entrada:e.target.value}))}>
                      {Array.from({length:Math.min(12,parseInt(todasFibras.find(f=>String(f.id)===String(splitterForm.fibra_entrada_id))?.tipo_hilos||12)-(parseInt(splitterForm.buffer_entrada||1)-1)*12)},(_,i)=>{
                        const h=i+1;
                        const abs=(parseInt(splitterForm.buffer_entrada||1)-1)*12+h;
                        const nom=["Azul","Naranja","Verde","Marron","Gris","Blanco","Rojo","Negro","Amarillo","Violeta","Rosado","Aqua"][i%12];
                        // Verificar si este hilo ya está ocupado — buscar en splitters Y fusiones
                        const ocupadoEnSplitter = data.splitters.some(s=>
                          String(s.fibra_entrada_id)===String(splitterForm.fibra_entrada_id)&&
                          parseInt(s.buffer_entrada)===parseInt(splitterForm.buffer_entrada||1)&&
                          parseInt(s.hilo_entrada)===h
                        );
                        const ocupadoEnFusion = data.fusiones.some(f=>
                          f.tipo==="splitter_entrada"&&
                          String(f.fibra_entrada_id)===String(splitterForm.fibra_entrada_id)&&
                          parseInt(f.buffer_entrada)===parseInt(splitterForm.buffer_entrada||1)&&
                          parseInt(f.hilo_entrada)===h
                        );
                        const ocupado = ocupadoEnSplitter || ocupadoEnFusion;
                        return <option key={h} value={h} disabled={ocupado}>{ocupado?"✗ ":""}H{abs} {nom}{ocupado?" (usado)":""}</option>;
                      })}
                    </select>
                    <div style={{fontSize:11,color:"#9CA3AF",marginBottom:4}}>Dirección</div>
                    <div style={{display:"flex",gap:4,marginBottom:8}}>
                      <button onClick={()=>setSplitterForm(x=>({...x,dir_entrada:"devolver"}))} style={{padding:"3px 10px",borderRadius:6,border:"1px solid #374151",background:splitterForm.dir_entrada==="devolver"?"#78350F":"transparent",color:splitterForm.dir_entrada==="devolver"?"#FBBF24":"#9CA3AF",fontSize:11,cursor:"pointer"}}>← Entrada (viene de OLT)</button>
                      <button onClick={()=>setSplitterForm(x=>({...x,dir_entrada:"adelante"}))} style={{padding:"3px 10px",borderRadius:6,border:"1px solid #374151",background:splitterForm.dir_entrada==="adelante"?"#064E3B":"transparent",color:splitterForm.dir_entrada==="adelante"?"#34D399":"#9CA3AF",fontSize:11,cursor:"pointer"}}>Salida → (viene de adelante)</button>
                    </div>
                  </>}
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <button onClick={guardarSplitter} disabled={saving} style={{...S.btnPrimary,flex:1,padding:"8px 0"}}>{saving?"...":"Guardar"}</button>
                    <button onClick={()=>setShowSplitterForm(false)} style={{...S.btnSec,flex:1,padding:"8px 0"}}>Cancelar</button>
                  </div>
                </div>
              )}
              {data.splitters.length===0&&!showSplitterForm&&<p style={{color:"#6B7280",fontSize:12,margin:0}}>Sin splitters.</p>}
              {data.splitters.map(sp=>(
                <div key={sp.id} style={{...S.fusionRow,flexDirection:"column",alignItems:"flex-start",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",width:"100%",alignItems:"center"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={S.splitterBox}><p style={{margin:0,fontSize:15,fontWeight:700,color:"#C4B5FD"}}>{sp.tipo}</p><p style={{margin:0,fontSize:9,color:"#7C3AED"}}>N{sp.nivel}</p></div>
                      <div>
                        <p style={{margin:0,fontSize:12,color:"#F9FAFB"}}>Nivel {sp.nivel} — {PERDIDAS[sp.tipo]} dB pérdida</p>
                        {sp.fibra_entrada_id&&<p style={{margin:0,fontSize:11,color:"#60A5FA"}}>Entrada: H{(parseInt(sp.buffer_entrada||1)-1)*12+parseInt(sp.hilo_entrada||1)} · {todasFibras.find(f=>String(f.id)===String(sp.fibra_entrada_id))?.nombre||"?"}</p>}
                        {sp.splitter_padre_id&&<p style={{margin:0,fontSize:11,color:"#9CA3AF"}}>Cascada desde splitter #{sp.splitter_padre_id} puerto {sp.puerto_padre}</p>}
                      </div>
                    </div>
                    {canEdit&&<button onClick={()=>eliminarSplitter(sp.id)} style={S.btnDanger}>X</button>}
                  </div>
                </div>
              ))}
            </div>
          </>}

          {/* FUSIONES */}
                    {tab==="fusiones"&&<>
            {/* Sub-tabs fusiones */}
            <div style={{display:"flex",gap:4,marginBottom:12,borderBottom:"1px solid #1e2a4a",paddingBottom:8}}>
              {["splitter","hilos","buffers"].map(st=>(
                <button key={st} onClick={()=>setSubTabFus(st)} style={{padding:"4px 12px",borderRadius:6,border:"none",background:subTabFus===st?"#7C3AED":"transparent",color:subTabFus===st?"#fff":"#9CA3AF",fontSize:12,cursor:"pointer",fontWeight:subTabFus===st?600:400}}>
                  {st==="splitter"?"1. Splitter":st==="hilos"?"2. Hilos":"3. Buffers"}
                </button>
              ))}
            </div>
            {subTabFus==="splitter"&&(
              <FusionSplitter
                key={`sp-${data.splitters.map(s=>s.id).join('-')}-${data.fusiones.filter(f=>f.tipo==='splitter_entrada'||f.tipo==='splitter_salida').map(f=>f.id).join('-')}`}
                mufa_id={mufa.id}
                splitters={data.splitters}
                fibras={todasFibras}
                fusionesExistentes={data.fusiones}
                onGuardado={cargar}
                canEdit={canEdit}
              />
            )}
            {subTabFus==="hilos"&&(
              <FusionHilos
                mufa_id={mufa.id}
                fibras={todasFibras}
                fusionesExistentes={data.fusiones}
                onGuardado={cargar}
                canEdit={canEdit}
              />
            )}
            {subTabFus==="buffers"&&(
              <FusionBuffers
                mufa_id={mufa.id}
                fibras={todasFibras}
                fusionesExistentes={data.fusiones}
                onGuardado={cargar}
                canEdit={canEdit}
              />
            )}
          </>}

                    {/* FIBRAS */}
          {tab==="fibras"&&<>
            <div style={S.card}>
              <div style={S.cardTitle}>Mapa óptico — {mufa.nombre} · {todasFibras.length} fibra(s)</div>
              <DiagramaRed
                fibras={todasFibras}
                fusiones={data.fusiones}
                splitters={data.splitters}
                fibrasEntrada={data.fibras_entrada||[]}
                fibrasSalida={data.fibras_salida||[]}
                fibrasPaso={data.fibras_paso||[]}
              />
              <div style={{marginTop:8}}>
                <DiagramaFibra
                  fibras={todasFibras}
                  fusiones={data.fusiones}
                  splitters={data.splitters}
                  fibrasEntrada={data.fibras_entrada||[]}
                  fibrasSalida={data.fibras_salida||[]}
                  fibrasPaso={data.fibras_paso||[]}
                />
              </div>
            </div>
          </>}

          {/* OPTICO */}
          {tab==="optico"&&<>
            <div style={S.card}>
              <div style={S.cardTitle}>Presupuesto optico</div>
              {data.presupuesto.length>0?data.presupuesto.map((p,i)=>(
                <div key={i}>
                  <div style={S.row}><span style={S.label}>{p.concepto}</span><span style={p.tipo==="ok"?S.valOk:p.tipo==="warn"?S.valWarn:p.tipo==="perdida"?S.valWarn:S.valOk}>{p.valor}</span></div>
                  {(p.tipo==="ok"||p.tipo==="warn")&&<div style={S.divider}/>}
                </div>
              )):<p style={{color:"#6B7280",fontSize:12,margin:0}}>Sin datos.</p>}
            </div>
            <div style={S.card}>
              <div style={S.cardTitle}>Nota</div>
              <p style={{fontSize:11,color:"#6B7280",margin:0}}>Potencia a la salida de la mufa. Cada NAP resta adicionalmente según su splitter.</p>
            </div>
          </>}

          {/* RECORRIDO */}
          {tab==="recorrido"&&<>
            <div style={S.card}>
              <div style={S.cardTitle}>Trazabilidad de red</div>
              {data.recorrido.length>0?(
                <div style={{display:"flex",flexDirection:"column"}}>
                  {data.recorrido.map((eq,i)=>(
                    <div key={i}>
                      <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0"}}>
                        <div style={{width:32,height:32,borderRadius:"50%",background:eq.tipo==="olt"?"#1E3A8A":eq.tipo==="mufa"?"#4C1D95":eq.tipo==="nap"?"#064E3B":"#78350F",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff",fontWeight:700,flexShrink:0}}>
                          {eq.tipo==="olt"?"OLT":eq.tipo==="mufa"?"MUF":eq.tipo==="nap"?"NAP":"POS"}
                        </div>
                        <div style={{flex:1}}>
                          <p style={{margin:0,fontSize:13,color:"#F9FAFB",fontWeight:500}}>{eq.nombre}</p>
                          <p style={{margin:0,fontSize:11,color:"#6B7280"}}>{eq.tipo.toUpperCase()}</p>
                        </div>
                        {eq.tipo==="mufa"&&parseInt(eq.id)===parseInt(mufa.id)&&<span style={{fontSize:10,padding:"2px 8px",borderRadius:99,background:"#4C1D95",color:"#C4B5FD"}}>actual</span>}
                      </div>
                      {i<data.recorrido.length-1&&<div style={{width:2,height:16,background:"#2a2a4a",marginLeft:15}}/>}
                    </div>
                  ))}
                </div>
              ):<p style={{color:"#6B7280",fontSize:12,margin:0}}>No se detecto recorrido.</p>}
            </div>
          </>}

        </div>
      )}
    {editorOpen && <EditorRed mufa={mufa} fibras={todasFibras} onClose={()=>setEditorOpen(false)}/>}
    </div>
  );
}

